import { useQuery } from '@tanstack/react-query'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Panel, Blank } from '../components/ui/panel'
import { Chip } from '../components/ui/tone'
import { api } from '../lib/api'

/**
 * Warmup (Phase 5, §20).
 *
 * Domain reputation takes time. New domains must start with low volume and gradually increase
 * to train receiving mailservers that the domain is legitimate. Warmup shows progress and
 * recommendations.
 */

interface WarmupDomain {
  id: string
  fqdn: string
  age: number // days since first send
  phase: 1 | 2 | 3 | 4 | 5
  volume: {
    today: number
    limit: number
    recommended: number
  }
  status: 'on_track' | 'ahead' | 'behind'
  nextPhase?: string
}

interface WarmupData {
  domains: WarmupDomain[]
  activeCount: number
  readyCount: number
}

const PHASE_LABEL: Record<number, { name: string; description: string }> = {
  1: { name: 'Phase 1', description: 'Days 1–3: 50 per day' },
  2: { name: 'Phase 2', description: 'Days 4–7: 200 per day' },
  3: { name: 'Phase 3', description: 'Days 8–14: 500 per day' },
  4: { name: 'Phase 4', description: 'Days 15–21: 1000 per day' },
  5: { name: 'Phase 5', description: 'Day 22+: Unrestricted' },
}

const DEFAULT_PHASE = { name: 'Phase 5', description: 'Day 22+: Unrestricted' }

export function WarmupScreen() {
  const warmup = useQuery({
    queryKey: ['warmup'],
    queryFn: () => api.get<WarmupData>('/sending/warmup'),
    refetchInterval: 300000, // Poll every 5 minutes
  })

  const data = warmup.data

  return (
    <Screen>
      <ScreenHeader
        title="Warmup"
        description="Domain reputation building schedule"
      />

      <div className="space-y-4">
        {/* Summary */}
        {data && (
          <Panel title="Status" description={`${data.domains.length} domain${data.domains.length !== 1 ? 's' : ''}`}>
            {data.domains.length === 0 ? (
              <Blank
                icon="domains"
                title="No domains in warmup"
                body="Domains start warmup after their first verified send. The schedule then automatically restricts and lifts send limits based on age and reputation."
              />
            ) : (
              <div className="flex gap-6 px-4 py-3">
                <div>
                  <div className="text-muted text-[11px]">Active</div>
                  <div className="text-text mt-1 text-[15px] font-semibold tabular-nums">
                    {data.activeCount}
                  </div>
                </div>
                <div>
                  <div className="text-muted text-[11px]">Ready</div>
                  <div className="text-text mt-1 text-[15px] font-semibold tabular-nums">
                    {data.readyCount}
                  </div>
                </div>
              </div>
            )}
          </Panel>
        )}

        {/* Domains */}
        {data && data.domains.length > 0 && (
          <Panel title="Domains">
            <div className="divide-line2 divide-y">
              {data.domains.map((domain) => {
                const phase = PHASE_LABEL[domain.phase] || DEFAULT_PHASE
                const percentUsed = Math.round((domain.volume.today / domain.volume.limit) * 100)

                return (
                  <div key={domain.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-mono text-[13px] text-text">{domain.fqdn}</h4>
                          <Chip
                            tone={
                              domain.status === 'on_track'
                                ? 'ok'
                                : domain.status === 'ahead'
                                  ? 'info'
                                  : 'warn'
                            }
                          >
                            {domain.status === 'on_track'
                              ? 'On track'
                              : domain.status === 'ahead'
                                ? 'Ahead'
                                : 'Behind'}
                          </Chip>
                        </div>

                        {/* Phase and age */}
                        <div className="mt-2 text-[12px]">
                          <p className="text-muted">
                            {phase.name} · {phase.description} · {domain.age} day{domain.age !== 1 ? 's' : ''} old
                          </p>
                        </div>

                        {/* Usage bar */}
                        <div className="mt-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-muted text-[11px]">Daily limit</span>
                            <span className="text-text text-[11px] tabular-nums">
                              {domain.volume.today} / {domain.volume.limit}
                            </span>
                          </div>
                          <div className="bg-line h-[6px] rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                percentUsed > 90
                                  ? 'bg-bad'
                                  : percentUsed > 70
                                    ? 'bg-warn'
                                    : 'bg-ok'
                              }`}
                              style={{ width: `${Math.min(percentUsed, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Recommendation */}
                        {domain.nextPhase && (
                          <div className="mt-2 text-[11px] text-accent">
                            {domain.nextPhase}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </Panel>
        )}

        {/* How warmup works */}
        <Panel
          title="How warmup works"
          description="§20 reputation building"
        >
          <div className="space-y-3 px-4 py-3 text-[12.5px]">
            <p className="text-text">
              New domains start with strict send limits and gradually increase to train receiving
              mailservers.
            </p>
            <ul className="space-y-2 text-muted list-none">
              <li className="flex gap-2">
                <span className="text-accent font-semibold">•</span>
                <span>
                  <strong className="text-text">Phase 1 (Days 1–3):</strong> 50 sends per day
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-semibold">•</span>
                <span>
                  <strong className="text-text">Phase 2 (Days 4–7):</strong> 200 sends per day
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-semibold">•</span>
                <span>
                  <strong className="text-text">Phase 3 (Days 8–14):</strong> 500 sends per day
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-semibold">•</span>
                <span>
                  <strong className="text-text">Phase 4 (Days 15–21):</strong> 1000 sends per day
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-semibold">•</span>
                <span>
                  <strong className="text-text">Phase 5 (Day 22+):</strong> No automatic limits
                </span>
              </li>
            </ul>
            <p className="text-muted mt-3">
              Sending ahead of schedule improves reputation faster. Falling behind may indicate
              deliverability issues.
            </p>
          </div>
        </Panel>
      </div>
    </Screen>
  )
}
